import {
  Box,
  Typography,
  Stack,
  IconButton,
  Divider,
  CircularProgress,
  Fade,
  Alert,
  SwipeableDrawer,
  Button,
  useTheme,
  Tooltip,
  useMediaQuery,
  Slide,
} from "@mui/material";
import strings from "global/constants/StringConstants";
import {
  getCallbackKeyFromUrl,
  handleErrorMessage,
  isTruthy,
} from "helpers/methods";
import { useAppSelector } from "hooks/use-app-select";
import { RequestFile, RequestParticipant, Reviewers } from "models/interfaces";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { RootState } from "utils/store";
import StartSigning from "assets/icons1/StartSigningIcon.svg";
import { fieldColor } from "../SignRequestHelper";
import {
  FIELD_COMMIT_EVENT,
  SignerState,
  fieldsRemaining,
  downloadCombinedFileAsZip,
} from "./ParticipantScreenService";
import { globalEmitter } from "utils/emitter";
import {
  fetchDocumentSummaryCallback,
  saveFieldData,
} from "../SignRequestService";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import DownloadIcon from "@mui/icons-material/Download";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import AiIcon from "assets/icons1/AiStar.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllRoundedIcon from "@mui/icons-material/DoneAllRounded";
import DescriptionIcon from "@mui/icons-material/Description";
import SignIcon from "assets/icons1/SignIcon.svg";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ParticipantDocumentViewer from "./ParticipantDocumentViewer";
import Documents from "assets/icons1/Documents.svg";
import DownloadDocs from "assets/icons1/DownloadDocs.svg";
import { setReduxSelectedFile, setReduxSignRequest } from "redux/appSlice";
import { useAppDispatch } from "redux/hooks";
import history from "utils/history";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CustomDropdownButton from "global/components/CustomDropdownButton/CustomDropdownButton";
import Delegate from "assets/icons1/DelegateIcon.svg";
import Decline from "assets/icons1/DeclineICon.svg";
import {
  AutoAwesome,
  Article,
  Description,
  Insights,
} from "@mui/icons-material";
import ChatWithPdf from "../components/ChatWithPdf";
import AIInsightsPanel from "../components/AIInsightsPanel";
import DocumentsComponent from "../components/Documents";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureGateBlock from "global/components/FeatureGateBlock/FeatureGateBlock";
import ReviewerAction from "./ApproverFields";
import MobileAiAssistantSheet from "../Mobile/components/MobileAiAssistantSheet";
import MobileDocumentsSheet from "../Mobile/components/MobileDocumentsSheet";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { embedAllowsDownload } from "embed/embedMode";

const ParticipantMobileScreen = (props: {
  state: SignerState;
  dispatch: Function;
  loading: boolean;
  setLoading: Function;
  selectedFile: RequestFile;
  setSelectedFile: Function;
  fileSrc: string;
  currentPageNumber: number;
  setCurrentPageNumber: Function;
  handleFileChange: Function;
  handleSubmit: Function;
  downloadFile: Function;
  currentReviewer: RequestParticipant;
  handleApproveRejectRequest: Function;
  reqToken: string;
  currentFieldOnFocus: number;
  setCurrentFieldOnFocus: Function;
  moveToFieldLocation: Function;
  signingJourneyOn: boolean;
  setSigningJourneyOn: Function;
  attachSingleCertificate: boolean;
  docLoadSuccess: boolean;
  setDocLoadSuccess: Function;
  docLoading: boolean;
  setDocLoading: Function;
  allFieldsFilled: boolean;
  allRequiredFieldsFilled: boolean;
  downloadDocs: Function;
  downloadDocsAsZip: Function;
  isDownloadingZip?: boolean;
  setReasonOpen: (value: boolean) => void;
  setDialogType: (value: string) => void;
  setDelegationEmail: (value: string) => void;
  allowDelegation: boolean;
}) => {
  const { selectedParticipant } = useAppSelector(
    (state: RootState) => state.app,
  );

  const urlParams = window.location.search;
  const reqToken = new URLSearchParams(urlParams).get("id");
  const requestStatus = props?.state?.signRequest?.status;
  const [isClicked, setIsClicked] = useState(false);
  const [, setShowAlert] = useState<boolean>(!props.allFieldsFilled);
  const [aiLoader, setAiLoader] = useState<boolean>(false);
  const [openSummary, setOpenSummary] = useState<boolean>(false);
  const [summary, setSummary] = useState<any>();
  // Counter for the event-driven batch save below. Increments on every
  // field commit (text blur with change, checkbox toggle, signature complete,
  // etc.) and resets after each successful saveFieldData call.
  const commitCountRef = useRef<number>(0);
  // Mirrors the latest signRequest so the emitter listener can read it
  // without re-subscribing on every signRequest change.
  const signRequestRef = useRef(props.state.signRequest);
  const [panelOpen, setPanelOpen] = useState<boolean>(false);
  const [openAiSheet, setOpenAiSheet] = useState<boolean>(false);
  const [openDocsSheet, setOpenDocsSheet] = useState<boolean>(false);
  const [activeRightPanel, setActiveRightPanel] = useState<
    "chat" | "insights" | "documents" | null
  >(null);
  const [viewportHeight, setViewportHeight] = useState<number>(
    typeof window !== "undefined" ? window.innerHeight : 0,
  );
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const currentSigner = props.state.signRequest.signers.find(
    (s) => s.email === selectedParticipant.email,
  );

  const aiAssistantState = useFeatureState(FeatureKeys.AI_ASSISTANT);
  const isAIAvailable =
    aiAssistantState.status === strings.FEATURE_STATUS_AVAILABLE;

  // Right panel icons configuration
  const rightPanelIcons = [
    {
      id: "documents" as const,
      icon: Description,
      label: "Documents",
      title: "Documents",
    },
    {
      id: "chat" as const,
      icon: AutoAwesome,
      label: "Ask AI Assistant",
      title: "Generative summary",
    },
    {
      id: "insights" as const,
      icon: Insights,
      label: "Insights",
      title: "AI Insights",
    },
  ];

  const handleRightPanelChange = useCallback(
    (panel: "chat" | "insights" | "documents") => {
      setActiveRightPanel(panel);
      setPanelOpen(true); // Open the drawer when an icon is clicked
    },
    [],
  );

  const getSelectedFileValue = () => {
    const path =
      props.selectedFile?.unsignedPdf || props.selectedFile?.unsignedDoc || "";
    const parts = path.split("/");
    return parts[1] ?? parts[0] ?? "";
  };

  const [value, setValue] = useState<String>(getSelectedFileValue());

  useEffect(() => {
    setValue(getSelectedFileValue());
  }, [props.selectedFile]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (panelOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      // Disable body scroll
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable body scroll
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
      }
    }

    // Cleanup function
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [panelOpen]);

  useEffect(() => {
    // Set signRequest in Redux for Documents component
    dispatch(setReduxSignRequest(props.state.signRequest));
  }, [props.state.signRequest, dispatch]);

  // Keep signRequestRef in sync so the commit listener always sees the
  // latest snapshot when it fires saveFieldData.
  useEffect(() => {
    signRequestRef.current = props.state.signRequest;
  }, [props.state.signRequest]);

  // Event-driven batch save: every COMMITS_PER_SAVE field commits, fire
  // saveFieldData once and reset the counter. No timers — purely
  // event-driven so we don't introduce dirty-read/write races between a
  // scheduled save and the next fetch.
  useEffect(() => {
    const COMMITS_PER_SAVE = 5;
    const handleCommit = () => {
      const signRequest = signRequestRef.current;
      const isPending =
        signRequest?.status === strings.pending &&
        signRequest?.files?.length > 0;
      if (!reqToken || !isPending) return;

      commitCountRef.current += 1;
      if (commitCountRef.current >= COMMITS_PER_SAVE) {
        commitCountRef.current = 0;
        saveFieldData(signRequest, reqToken).catch((err) =>
          console.error("Error saving field data:", err),
        );
      }
    };

    globalEmitter.on(FIELD_COMMIT_EVENT, handleCommit);
    return () => {
      globalEmitter.off(FIELD_COMMIT_EVENT, handleCommit);
    };
  }, [reqToken]);

  useEffect(() => {
    // Set initial selected file in Redux for Documents component
    if (props.selectedFile) {
      const normalized: any = {
        ...props.selectedFile,
        unsignedDoc:
          (props.selectedFile as any).unsignedDoc ||
          (props.selectedFile as any).unsignedPdf ||
          "",
        unsignedDocUrl:
          (props.selectedFile as any).unsignedDocUrl ||
          (props.selectedFile as any).unsignedPdfUrl ||
          "",
      };
      dispatch(setReduxSelectedFile(normalized));
    }
  }, [props.selectedFile, dispatch]);

  // Handle Safari viewport height changes (address bar show/hide)
  useEffect(() => {
    const handleResize = () => {
      setViewportHeight(window.innerHeight);
    };

    // Use visualViewport API if available (better for Safari)
    if (window.visualViewport) {
      const handleViewportChange = () => {
        setViewportHeight(window.visualViewport?.height || window.innerHeight);
      };
      window.visualViewport.addEventListener("resize", handleViewportChange);
      window.visualViewport.addEventListener("scroll", handleViewportChange);
      return () => {
        window.visualViewport?.removeEventListener(
          "resize",
          handleViewportChange,
        );
        window.visualViewport?.removeEventListener(
          "scroll",
          handleViewportChange,
        );
      };
    } else {
      window.addEventListener("resize", handleResize);
      window.addEventListener("orientationchange", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("orientationchange", handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (props.selectedFile?.unsignedPdf) {
      setValue(
        props.selectedFile.unsignedPdf.split("/")[1] ??
          props.selectedFile.unsignedPdf.split("/")[0],
      );
    }
  }, [props.selectedFile]);

  useEffect(() => {
    if (isClicked && !props.allRequiredFieldsFilled) {
      const timer = setTimeout(() => {
        setIsClicked(false);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isClicked, props.allRequiredFieldsFilled]);

  const handleFinishAction = () => {
    if (!props.allRequiredFieldsFilled) {
      setIsClicked(true);
      props.setSigningJourneyOn(true);
      props.moveToFieldLocation(1, true);
    } else {
      props.handleSubmit();
    }
  };
  const saveRequest = async () => {
    try {
      const response = await saveFieldData(props.state.signRequest, reqToken!);
    } catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const getDocumentSummary = async () => {
    try {
      setAiLoader(true);
      const summary = await fetchDocumentSummaryCallback(
        props.reqToken,
        props.selectedFile,
      );
      if (summary) {
        setSummary(summary);
      }
    } catch (error: any) {
      handleErrorMessage(error);
      setAiLoader(false);
    } finally {
      setAiLoader(false);
    }
  };

  const showSummary = (data: any, level = 0) => {
    if (summary === undefined) return <></>;
    return Object.keys(data).map((key) => {
      if (typeof data[key] === "object") {
        return (
          <Box
            key={key}
            sx={{ marginBottom: "20px", paddingLeft: `${level * 10}px` }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                marginBottom: "10px",
                textDecoration: "underline",
                fontSize: "1rem",
              }}
            >
              {key}
            </Typography>
            <ul style={{ paddingLeft: "15px" }}>
              {showSummary(data[key], level + 1)}{" "}
            </ul>
          </Box>
        );
      } else {
        return (
          <li key={key}>
            <Typography variant="body1" sx={{ fontSize: "0.9rem" }}>
              {data[key]}
            </Typography>
          </li>
        );
      }
    });
  };

  // Function to recursively render summary content with improved formatting
  const renderSummaryContent = (data: any, level = 0) => {
    if (!data) return null;

    return Object.entries(data).map(([key, value]) => {
      if (typeof value === "object") {
        return (
          <Box
            key={key}
            sx={{
              mb: 3,
              pl: level * 1.5,
              borderLeft: level > 0 ? "2px solid rgba(0,0,0,0.1)" : "none",
              paddingLeft: level > 0 ? 2 : 0,
              textAlign: "left",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 1.5,
                fontSize: "1.1rem",
                color: "#333",
              }}
            >
              {key}
            </Typography>
            <Box sx={{ pl: 2.5, m: 0 }}>
              {renderSummaryContent(value, level + 1)}
            </Box>
          </Box>
        );
      } else {
        return (
          <Box
            key={key}
            sx={{
              mb: 1,
              pl: 1.5,
              position: "relative",
              "&::before": {
                content: '"•"',
                position: "absolute",
                left: 0,
                color: "#555",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.95rem",
                lineHeight: 1.5,
                color: "#444",
              }}
            >
              {typeof value === "string" ? value : ""}
            </Typography>
          </Box>
        );
      }
    });
  };

  const shouldShowFinishButton = () => {
    return (
      !isTruthy(props.currentReviewer.email) &&
      (props.state.signRequest.status === strings.pending ||
        props.state.signRequest.status === strings.signed) &&
      (props.state.signRequest.signRequestConfig?.allowDocumentDownload ||
        !props.fileSrc)
    );
  };

  const headerContent = () => {
    return (
      <Box
        component="header"
        sx={{
          backdropFilter: "blur(6px)",
          position: "sticky",
          top: 0,
          zIndex: 1100,
          width: "100%",
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          justifyContent="space-between"
          spacing={1}
          sx={{
            minHeight: 50,
            p: 1,
            background: (t) => t.palette.background.paper,
          }}
        >
          {!isTruthy(props.currentReviewer.email) &&
            !isTruthy(props.fileSrc) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  px: { xs: 1, sm: 2 },
                  py: 1.5,
                  backgroundColor: "background.paper",
                  borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
                }}
              >
                <Stack
                  direction="column"
                  alignItems="center"
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "100%", sm: "400px" },
                  }}
                >
                  {/* Fields remaining counter */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.25,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        fontSize: "0.8125rem",
                      }}
                    >
                      Fields remaining:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: theme.palette.signer.orange.main,
                        fontSize: "0.8125rem",
                      }}
                    >
                      {(() => {
                        const fields = fieldsRemaining(
                          props.state.signRequest.files,
                        );
                        const remaining =
                          fields.requiredValue - fields.requiredWithValue;
                        return `${remaining} / ${fields.requiredValue}`;
                      })()}
                    </Typography>
                  </Box>

                  {/* Navigation and Start Signing */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip
                      describeChild
                      title={
                        props.currentFieldOnFocus <= 1
                          ? "No previous field"
                          : `${strings.previous} Field`
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          // The Tooltip names the wrapping span, not the button (4.1.2).
                          aria-label="Previous field"
                          onClick={() => {
                            if (!(props.currentFieldOnFocus <= 1))
                              props.moveToFieldLocation(
                                props.currentFieldOnFocus - 1,
                              );
                          }}
                          disabled={props.currentFieldOnFocus <= 1}
                          sx={{
                            width: 28,
                            height: 28,
                            border: "1px solid",
                            borderColor: "signer.border.main",
                            backgroundColor: "background.paper",
                            "&:hover": {
                              backgroundColor: "signer.surface.subtle",
                            },
                            "&:disabled": {
                              borderColor: "signer.border.main",
                              backgroundColor: "signer.surface.subtle",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <ChevronLeftRoundedIcon
                            sx={{
                              fontSize: 18,
                              color:
                                props.currentFieldOnFocus <= 1
                                  ? theme.palette.signer.text.muted
                                  : theme.palette.signer.ink,
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Button
                      variant="contained"
                      size="small"
                      startIcon={
                        <img
                          src={StartSigning}
                          alt="Start"
                          style={{ width: 14, height: 14 }}
                        />
                      }
                      onClick={() => {
                        props.setSigningJourneyOn(true);
                        props.moveToFieldLocation(1, true);
                      }}
                      disabled={
                        props.docLoading ||
                        isTruthy(props.currentReviewer.email) ||
                        requestStatus !== strings.pending ||
                        props.signingJourneyOn ||
                        Boolean(props.fileSrc)
                      }
                      sx={{
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.8125rem",
                        height: 38,
                        borderRadius: "9px",
                        boxShadow: "none",
                        backgroundColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Start Signing
                    </Button>

                    <Tooltip
                      describeChild
                      title={
                        props.currentFieldOnFocus >=
                        fieldsRemaining(props.state.signRequest.files)
                          .totalFields
                          ? "No next field"
                          : `${strings.next} Field`
                      }
                      arrow
                    >
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Next field"
                          onClick={() => {
                            const totalFields = fieldsRemaining(
                              props.state.signRequest.files,
                            ).totalFields;
                            if (props.currentFieldOnFocus >= totalFields) return;
                            props.moveToFieldLocation(
                              props.currentFieldOnFocus + 1,
                            );
                          }}
                          disabled={
                            props.currentFieldOnFocus >=
                            fieldsRemaining(props.state.signRequest.files)
                              .totalFields
                          }
                          sx={{
                            width: 28,
                            height: 28,
                            border: "1px solid",
                            borderColor: "signer.border.main",
                            backgroundColor: "background.paper",
                            "&:hover": {
                              backgroundColor: "signer.surface.subtle",
                            },
                            "&:disabled": {
                              borderColor: "signer.border.main",
                              backgroundColor: "signer.surface.subtle",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <ChevronRightRoundedIcon
                            sx={{
                              fontSize: 18,
                              color:
                                props.currentFieldOnFocus >=
                                fieldsRemaining(props.state.signRequest.files)
                                  .totalFields
                                  ? theme.palette.signer.text.muted
                                  : theme.palette.signer.ink,
                            }}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
            )}

          {props.fileSrc &&
            props.state.signRequest.signRequestConfig?.allowDocumentDownload &&
            !isTruthy(props.currentReviewer.email) &&
            (props.state.signRequest.status === strings.pending ||
              props.state.signRequest.status === strings.signed) && (
              // <CustomButton
              //   id={`signer-download-all-pdf-btn-mobile`}
              //   label={"Download as ZIP"}
              //   buttonType="primaryBtn"
              //   onClick={() => {
              //     setDrawerOpen(false);
              //     downloadCombinedFileAsZip(
              //       props.reqToken,
              //       props.state.signRequest,
              //       props.setLoading
              //     );
              //   }}
              //   customClasses={{
              //     height: "32px",
              //     minWidth: "auto",
              //     padding: "0 10px",
              //     backgroundColor: "#FF8C00",
              //   }}
              //   btnFontStyles={{ fontSize: "0.75rem" }}
              //   startIcon={<FolderZipIcon fontSize="small" />}
              // />
              <Button
                aria-label="Download all documents as ZIP"
                onClick={() => props.downloadDocsAsZip()}
                disabled={props.isDownloadingZip}
                startIcon={
                  props.isDownloadingZip ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <FolderZipIcon fontSize="small" />
                  )
                }
                size="medium"
              />
            )}
        </Stack>
      </Box>
    );
  };

  // Memoized bottom drawer content
  const BottomDrawerContent = useMemo(
    () => (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
        }}
      >
        {/* Content Panel */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          {/* Header with Icon and Close Button - visible only on mobile */}
          {isSmallScreen && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.5,
                // Safari safe area support - ensure header is visible above address bar
                paddingTop: {
                  xs: `calc(1.5rem + env(safe-area-inset-top, 0px))`,
                  sm: 1.5,
                },
                // borderBottom: "1px solid #E6E6E6",
                backgroundColor: "background.paper",
                flexShrink: 0,
                position: "sticky",
                top: 0,
                zIndex: 10,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {(() => {
                  const activeIcon = rightPanelIcons.find(
                    (p) => p.id === activeRightPanel,
                  );
                  const IconComponent = activeIcon?.icon || Description;
                  return (
                    <IconComponent
                      sx={{
                        fontSize: 20,
                        color: theme.palette.signer.orange.main,
                      }}
                    />
                  );
                })()}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    fontSize: "0.9rem",
                  }}
                >
                  {rightPanelIcons.find((p) => p.id === activeRightPanel)
                    ?.label || "Documents"}
                </Typography>
              </Box>
              <IconButton
                size="small"
                aria-label="Close panel"
                onClick={() => {
                  setPanelOpen(false);
                  setActiveRightPanel(null);
                }}
                sx={{
                  color: theme.palette.text.primary,
                  "&:hover": {
                    backgroundColor: "signer.surface.muted",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {activeRightPanel === "documents" && (
              <DocumentsComponent
                hideHeader={true}
                docLoading={props.docLoading}
                onFileChange={(file) => {
                  if (!props.docLoading) {
                    props.setDocLoading(true);
                    dispatch(setReduxSelectedFile(file));
                    props.handleFileChange(
                      file.unsignedPdf || file.unsignedDoc,
                    );
                  }
                  setPanelOpen(false);
                  setActiveRightPanel(null);
                }}
              />
            )}
            {activeRightPanel === "chat" &&
              (isAIAvailable ? (
                <ChatWithPdf
                  hideHeader={true}
                  signRequest={props.state.signRequest}
                  isPublic={true}
                  requesToken={props.reqToken}
                />
              ) : (
                <FeatureGateBlock
                  variant="inline"
                  status={
                    aiAssistantState.status ===
                    strings.FEATURE_STATUS_ADMIN_DISABLED
                      ? strings.FEATURE_STATUS_ADMIN_DISABLED
                      : strings.FEATURE_STATUS_NOT_IN_PLAN
                  }
                  disabledMessage={(aiAssistantState as any).disabledMessage}
                />
              ))}
            {activeRightPanel === "insights" && (
              <AIInsightsPanel
                signRequest={props.state.signRequest}
                isPublic={true}
                requesToken={props.reqToken}
                hideHeader={true}
              />
            )}
          </Box>
        </Box>
      </Box>
    ),
    [
      activeRightPanel,
      theme,
      props.docLoading,
      props.state.signRequest,
      props.reqToken,
      dispatch,
      isSmallScreen,
      rightPanelIcons,
      isAIAvailable,
      aiAssistantState,
    ],
  );

  return (
    <Box
      onClick={() => setShowAlert(false)}
      onKeyPress={(e) => {
        const target = e.target as HTMLElement;
        const isInputField =
          target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable;
        if (
          (e.key === "Enter" || e.key === " ") &&
          !isInputField
        ) {
          setShowAlert(false);
        }
      }}
      sx={(t) => ({
        "&:focus": {
          outline: `2px solid ${t.palette.signer.orange.main}`,
          outlineOffset: "2px",
        },
      })}
    >
      {isClicked && !props.allRequiredFieldsFilled && (
        <Fade in={isClicked} timeout={1000}>
          <Alert
            severity="warning"
            sx={{
              width: "fit-content",
              position: "fixed",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              fontSize: "0.8rem",
            }}
          >
            Please fill all required fields!
          </Alert>
        </Fade>
      )}

      <Box
        sx={{
          minHeight: "100vh",
          height: "100vh",
          width: "100%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          pb: panelOpen ? "90vh" : 0,
        }}
      >
        {/* Sticky header pinned to the top of the viewport column */}
        <Box sx={{ flexShrink: 0 }}>{headerContent()}</Box>

        {/* Documents switcher — opens the documents slide-up sheet */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: { xs: 1.5, sm: 2 },
            py: 1,
            backgroundColor: "background.paper",
          }}
        >
          <Box
            role="button"
            aria-label="View documents"
            tabIndex={0}
            onClick={() => setOpenDocsSheet(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setOpenDocsSheet(true);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "signer.orange.main",
              cursor: "pointer",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "signer.orange.main",
                outlineOffset: "2px",
              },
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 700 }}>
              Documents
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        {/* Document viewer on a gray canvas — matches the editor's mobile view
            (centered pages, hidden scrollbar). The viewer's own #pdf_box handles
            scrolling. */}
        <Box
          component="main"
          id="main-content"
          tabIndex={-1}
          aria-label="Document to sign"
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: "hidden",
            outline: "none",
            bgcolor: theme.palette.signer.surface.canvas,
            py: 1.5,
            px: 1.5,
            boxSizing: "border-box",
            "& #pdf_box": {
              alignItems: "center",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none !important",
                width: "0 !important",
              },
            },
            "& .react-pdf__Document": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            },
          }}
        >
          <Typography
            component="h1"
            sx={{
              position: "absolute",
              width: "1px",
              height: "1px",
              p: 0,
              m: "-1px",
              overflow: "hidden",
              clip: "rect(0 0 0 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Review and sign document
          </Typography>
          {isTruthy(props.fileSrc) ? (
                <ParticipantDocumentViewer
                  docLoading={props.docLoading}
                  setDocLoading={props.setDocLoading}
                  docLoadSuccess={props.docLoadSuccess}
                  setDocLoadSuccess={props.setDocLoadSuccess}
                  signerSelectedFile={props.fileSrc}
                  setSignerSelectedFile={props.setSelectedFile}
                  requestToken={props.reqToken!}
                  state={props.state}
                  setLoading={props.setLoading}
                  dispatch={props.dispatch}
                  selectedFile={props.selectedFile}
                  setSelectedFile={props.setSelectedFile}
                  currentReviewer={props.currentReviewer}
                  currentPageNumber={props.currentPageNumber}
                  setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
                  currentFieldOnFocus={props.currentFieldOnFocus}
                  moveToFieldLocation={props.moveToFieldLocation}
                  signingJourneyOn={props.signingJourneyOn}
                  loading={props.loading}
                  fileSrc={props.fileSrc}
                  setCurrentPageNumber={props.setCurrentPageNumber}
                  handleFileChange={props.handleFileChange}
                  handleSubmit={props.handleSubmit}
                  downloadFile={props.downloadFile}
                  handleApproveRejectRequest={props.handleApproveRejectRequest}
                  reqToken={props.reqToken!}
                  setSigningJourneyOn={props.setSigningJourneyOn}
                  attachSingleCertificate={props.attachSingleCertificate}
                  allFieldsFilled={props.allFieldsFilled}
                  isMobileScreen={true}
                />
              ) : (
                <ParticipantDocumentViewer
                  docLoading={props.docLoading}
                  setDocLoading={props.setDocLoading}
                  docLoadSuccess={props.docLoadSuccess}
                  setDocLoadSuccess={props.setDocLoadSuccess}
                  signerSelectedFile={props.selectedFile}
                  setSignerSelectedFile={props.setSelectedFile}
                  requestToken={props.reqToken!}
                  state={props.state}
                  setLoading={props.setLoading}
                  dispatch={props.dispatch}
                  selectedFile={props.selectedFile}
                  setSelectedFile={props.setSelectedFile}
                  currentReviewer={props.currentReviewer}
                  currentPageNumber={props.currentPageNumber}
                  setCurrentFieldOnFocus={props.setCurrentFieldOnFocus}
                  currentFieldOnFocus={props.currentFieldOnFocus}
                  moveToFieldLocation={props.moveToFieldLocation}
                  signingJourneyOn={props.signingJourneyOn}
                  loading={props.loading}
                  fileSrc={props.fileSrc}
                  setCurrentPageNumber={props.setCurrentPageNumber}
                  handleFileChange={props.handleFileChange}
                  handleSubmit={props.handleSubmit}
                  downloadFile={props.downloadFile}
                  handleApproveRejectRequest={props.handleApproveRejectRequest}
                  reqToken={props.reqToken!}
                  setSigningJourneyOn={props.setSigningJourneyOn}
                  attachSingleCertificate={props.attachSingleCertificate}
                  allFieldsFilled={props.allFieldsFilled}
                  isMobileScreen={true}
                />
              )}
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1, sm: 1.5 },
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1, sm: 1.5 },
            backgroundColor: "background.paper",
            borderTop: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: "visible",
              zIndex: 10,
              pt: { xs: 0.5, sm: 1 },
            }}
          >
            {props.state.signRequest.status === strings.pending &&
            currentSigner?.status === strings.pending ? (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: { xs: "flex-end", sm: "flex-end" },
                  position: "relative",
                  overflow: "visible",
                  zIndex: 10,
                }}
              >
                <CustomDropdownButton
                  handleMainClick={handleFinishAction}
                  buttonLabel={strings.finish}
                  openUpward={true}
                  sx={{
                    zIndex: 1400,
                    width: { xs: "100%", sm: "auto" },
                    "& .MuiButtonGroup-root": {
                      width: { xs: "100%", sm: "auto" },
                      "& .MuiButton-root": {
                        width: { xs: "100%", sm: "auto" },
                        minHeight: { xs: "40px", sm: "36px" },
                        fontSize: { xs: "0.875rem", sm: "0.8rem" },
                      },
                    },
                  }}
                  menuItems={[
                    ...(props.allowDelegation
                      ? [
                          {
                            icon: (
                              <img
                                src={Delegate}
                                alt=""
                                aria-hidden="true"
                                style={{ color: "#374151" }}
                              />
                            ),
                            label: `${strings.delegate} to other`,
                            onClick: () => {
                              props.setDialogType(strings.delegate);
                              props.setReasonOpen(true);
                            },
                          },
                        ]
                      : []),
                    {
                      icon: <img src={Decline} alt="" aria-hidden="true" />,
                      label: `${strings.decline} request`,
                      onClick: () => {
                        const urlParams = window.location.search;
                        const reqToken = new URLSearchParams(urlParams).get(
                          "id",
                        );
                        if (reqToken) {
                          history.push(
                            `/decline/request?id=${reqToken}&key=${encodeURIComponent(getCallbackKeyFromUrl())}&signer=true`,
                          );
                        } else {
                          props.setDialogType(strings.decline);
                          props.setReasonOpen(true);
                        }
                      },
                    },
                  ]}
                  disabled={props.loading || props.docLoading}
                />
              </Box>
            ) : props.state.signRequest.status === strings.inReview &&
              isTruthy(props.currentReviewer.email) ? (
              <Box>
                <ReviewerAction
                  requestStatus={props.state.signRequest.status}
                  currentReviewer={props.currentReviewer!}
                  reviewers={props.state.signRequest.approval.reviewers}
                  handleApproveRejectRequest={props.handleApproveRejectRequest}
                />
              </Box>
            ) : props.state.signRequest.status === strings.signed &&
              embedAllowsDownload(props.state.signRequest) ? (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
                sx={{ width: "100%" }}
              >
                <Button
                  variant="outlined"
                  size={isSmallScreen ? "small" : "medium"}
                  fullWidth={isSmallScreen}
                  startIcon={
                    <img
                      src={DownloadDocs}
                      alt=""
                      aria-hidden="true"
                      style={{
                        width: isSmallScreen ? "14px" : "16px",
                        height: isSmallScreen ? "14px" : "16px",
                      }}
                    />
                  }
                  onClick={() => {
                    props.downloadDocs();
                  }}
                  sx={{
                    minHeight: { xs: "40px", sm: "36px" },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    textTransform: "none",
                  }}
                >
                  {strings.downloadDoc}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => props.downloadDocsAsZip()}
                  disabled={props.isDownloadingZip}
                  startIcon={
                    props.isDownloadingZip ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : undefined
                  }
                  size={isSmallScreen ? "small" : "medium"}
                  fullWidth={isSmallScreen}
                  sx={{
                    minHeight: { xs: "40px", sm: "36px" },
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    textTransform: "none",
                  }}
                >
                  {strings.downloadDocsAsZip}
                </Button>
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* Backdrop overlay when panel is open */}
      {panelOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1199,
            backdropFilter: "blur(2px)",
          }}
          onClick={() => setPanelOpen(false)}
        />
      )}

      {/* Sticky Bottom Panel with Content - with slide animation */}
      <Slide direction="up" in={panelOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            width: "100%",
            height: "90vh",
            maxHeight: "90vh",
            // Safari safe area support
            paddingTop: {
              xs: "env(safe-area-inset-top, 0px)",
              sm: 0,
            },
            // Use dynamic viewport height for Safari
            "@supports (height: 100dvh)": {
              height: "90dvh",
              maxHeight: "90dvh",
            },
            // Fallback for Safari: use actual viewport height
            ...(isSmallScreen &&
              viewportHeight > 0 && {
                maxHeight: `${viewportHeight * 0.9}px`,
              }),
            zIndex: 1200,
            backgroundColor: "background.paper",
            borderTop: `1px solid ${theme.palette.signer.border.main}`,
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            borderTopLeftRadius: { xs: 16, sm: 20 },
            borderTopRightRadius: { xs: 16, sm: 20 },
            // Ensure content is scrollable and visible
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {BottomDrawerContent}
        </Box>
      </Slide>

      {/* Circular AI FAB — opens the Inkfree AI sheet from the bottom
          (same as the request-creation editor). Hidden while the sheet is
          open so it doesn't sit on top of the drawer. */}
      {!openAiSheet && (
        <Box
          role="button"
          aria-label="Inkfree AI assistant"
          tabIndex={0}
          onClick={() => setOpenAiSheet(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpenAiSheet(true);
            }
          }}
          sx={{
            position: "fixed",
            right: 16,
            bottom: 96,
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: (t) =>
              `linear-gradient(135deg, ${t.palette.signer.orange.main}, ${t.palette.signer.orange.dark})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 6px 16px rgba(33,21,71,0.35)",
            // Below the MUI drawer (~1200) so it never overlays the sheet.
            zIndex: 1000,
            cursor: "pointer",
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "common.white",
              outlineOffset: "2px",
            },
          }}
        >
          <AutoAwesome sx={{ fontSize: 18, color: "common.white" }} />
        </Box>
      )}

      <MobileAiAssistantSheet
        open={openAiSheet}
        onClose={() => setOpenAiSheet(false)}
      />

      <MobileDocumentsSheet
        open={openDocsSheet}
        onClose={() => setOpenDocsSheet(false)}
        onSelectFile={(file) => {
          if (!props.docLoading) {
            props.setDocLoading(true);
            props.handleFileChange(
              (file as any).unsignedPdf || (file as any).unsignedDoc,
            );
          }
        }}
      />

      <SwipeableDrawer
        anchor="bottom"
        open={openSummary}
        onClose={() => {
          setOpenSummary(false);
        }}
        onOpen={() => {}}
      >
        <Box p={2}>
          <Box
            sx={{
              pt: 1,
              display: "flex",
              background: "linear-gradient(to right, #f9f9f9, #ffffff)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {/* Left Section (icon + title) */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: `${theme.palette.primary.main}15`,
                    mr: 1.5,
                  }}
                >
                  <DescriptionIcon
                    sx={{
                      color: theme.palette.primary.main,
                      fontSize: "1.2rem",
                    }}
                  />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1a2942",
                    fontSize: "1.25rem",
                  }}
                >
                  Document Summary
                </Typography>
              </Box>

              {/* Close Button on Right */}
              <IconButton
                onClick={() => setOpenSummary(false)}
                sx={{
                  width: "36px",
                  height: "36px",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "none",
                  },
                  "&:active": {
                    background: "none",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box
            sx={{
              mt: 2,
              p: 1,
              maxHeight: "60vh",
              overflowY: "auto",
              background: "#ffffff",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            {summary ? (
              renderSummaryContent(summary.summary)
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  py: 4,
                  opacity: 0.7,
                }}
              >
                <ErrorOutlineIcon
                  sx={{ fontSize: "2.5rem", color: "#646464", mb: 1.5 }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: "center",
                    color: "#646464",
                    fontSize: "0.9rem",
                  }}
                >
                  No summary data available
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              borderTop: "1px solid rgba(0,0,0,0.08)",
              pt: 1.5,
              background: "#f9f9f9",
            }}
          >
            <Box
              sx={{
                mx: 2,
                mb: 1.5,
                p: 2,
                borderRadius: "6px",
                background: "rgba(25, 118, 210, 0.04)",
                border: "1px solid rgba(25, 118, 210, 0.1)",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              <InfoOutlinedIcon
                sx={{ color: "#1976d2", mr: 1, mt: "2px", fontSize: "0.9rem" }}
              />
              <Typography
                fontSize="0.75rem"
                sx={{
                  fontStyle: "italic",
                  color: "#555",
                  lineHeight: 1.4,
                }}
              >
                This AI-generated summary is provided for convenience but may
                not reflect the full content of the original document.
              </Typography>
            </Box>
          </Box>
        </Box>
      </SwipeableDrawer>
    </Box>
  );
};

export default ParticipantMobileScreen;
